import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../styles/ProjectDetail.css';
import API_BASE from '../utils/api.js';
import { validateWebImageFile } from '../utils/imageUpload.js';
import { resolveProjectCoverUrl } from '../utils/imageUrl.js';

const VISUALLY_HIDDEN_FILE_INPUT_STYLE = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function getApiBase() {
  return API_BASE;
}

function storageKeyTodos(userId) {
  return `todos-${userId || 'anon'}`;
}
function loadTodosFromStorage(userId) {
  try {
    const raw = localStorage.getItem(storageKeyTodos(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveTodosToStorage(userId, todos) {
  try {
    localStorage.setItem(storageKeyTodos(userId), JSON.stringify(todos));
  } catch {}
}

function normalizeTodos(arr = []) {
  return (arr || []).map((t) => ({
    todoId: t.todoId || t.id || '',
    title: t.title || '',
    notes: t.notes || '',
    completed: !!t.completed,
    dueDate: t.dueDate || '',
    priority: t.priority || 'normal',
    assignees: Array.isArray(t.assignees)
      ? t.assignees.map((a) => (typeof a === 'string' ? a : a.email)).filter(Boolean)
      : [],
    projectId:
      t.projectId !== undefined && t.projectId !== null
        ? String(t.projectId)
        : t.project
          ? String(t.project)
          : '',
    createdAt: t.createdAt || '',
    updatedAt: t.updatedAt || '',
  }));
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function getInvoiceRemaining(inv) {
  const total = Number(inv?.totals?.grandTotal || inv?.totals?.total || 0);
  const paid = Number(inv?.totals?.paid || 0);
  return Math.max(0, Number(inv?.remainingBalance ?? total - paid));
}

function getTimeBillingRemaining(inv) {
  const total = Number(inv?.totals?.total || 0);
  return Math.max(0, Number(inv?.remainingBalance ?? total));
}

export default function ProjectDetailsDefault() {
  const { project, setProject } = useOutletContext() || {};
  const coverImageUrl = resolveProjectCoverUrl(project);
  const coverInputId = 'project-cover-upload-input';

  const projectSite = project?.projectStreet
    ? `${project.projectStreet}\n${project.projectCity}, ${project.projectState} ${project.projectZip}`
    : project?.projectSite || '';

  const billingAddress = project?.billingStreet
    ? `${project.billingStreet}\n${project.billingCity}, ${project.billingState} ${project.billingZip}`
    : project?.billingAddress || '';

  const customerName =
    project?.customerName ||
    project?.customerFullName ||
    `${project?.customerFirst || ''} ${project?.customerLast || ''}`.trim() ||
    '';

  const customerContact = project?.email || project?.customerEmail || project?.contactEmail || '';
  const customerPhone = project?.phone || project?.customerPhone || project?.contactPhone || '';

  const userId = localStorage.getItem('userId') || null;
  const projId = project?.id ?? project?.projectId ?? '';

  const [miniTodos, setMiniTodos] = useState([]);
  const [loadingTodos, setLoadingTodos] = useState(true);
  const [team, setTeam] = useState([]);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(true);

  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const [tbInvoices, setTbInvoices] = useState([]);
  const [loadingTB, setLoadingTB] = useState(true);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState('');
  const [coverDragActive, setCoverDragActive] = useState(false);

  const apiBase = getApiBase();

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiBase}/api/settings/${encodeURIComponent(userId)}`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setTeam(Array.isArray(data.team) ? data.team : []))
      .catch(() => setTeam([]));
  }, [apiBase, userId]);

  const assigneeName = (email) =>
    team.find((m) => m.email === email)?.name || team.find((m) => m.email === email)?.email || email;

  // =============================================
  // Load TODOS
  // =============================================
  useEffect(() => {
    let mounted = true;

    async function fetchProjectTodos() {
      setLoadingTodos(true);
      if (userId) {
        const scopedUrl = `${apiBase}/api/todos/${encodeURIComponent(userId)}${
          projId ? `?projectId=${encodeURIComponent(projId)}` : ''
        }`;
        try {
          let res = await fetch(scopedUrl, { credentials: 'include' });

          if (!res.ok && projId) {
            res = await fetch(`${apiBase}/api/todos/${encodeURIComponent(userId)}`, {
              credentials: 'include',
            });
          }

          if (res.ok) {
            const json = await res.json();
            const normalized = normalizeTodos(
              Array.isArray(json) ? json : json.Items || json.items || []
            );
            saveTodosToStorage(userId, normalized);

            const projectFiltered = projId
              ? normalized.filter((t) => String(t.projectId) === String(projId))
              : normalized;

            if (mounted) setMiniTodos(pickNearest(projectFiltered, 3));
            setLoadingTodos(false);
            return;
          }
        } catch (err) {
          console.warn('Project todos fetch failed; falling back to cache', err);
        }
      }

      try {
        const cached = loadTodosFromStorage(userId) || [];
        const normalized = normalizeTodos(cached);
        const projectFiltered = projId
          ? normalized.filter((t) => String(t.projectId) === String(projId))
          : normalized;

        if (mounted) setMiniTodos(pickNearest(projectFiltered, 3));
      } catch (err) {
        console.warn('Failed to read todos from cache', err);
        if (mounted) setMiniTodos([]);
      } finally {
        setLoadingTodos(false);
      }
    }

    fetchProjectTodos();
    return () => (mounted = false);
  }, [userId, projId, apiBase]);

  function pickNearest(list, n = 3) {
    const today = new Date();
    function dateScore(d) {
      if (!d) return Number.POSITIVE_INFINITY;
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;
      return Math.abs(parsed - today);
    }
    const withScores = (list || []).map((t) => ({ t, score: dateScore(t.dueDate) }));
    withScores.sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      const da = a.t.dueDate ? new Date(a.t.dueDate).getTime() : Number.POSITIVE_INFINITY;
      const db = b.t.dueDate ? new Date(b.t.dueDate).getTime() : Number.POSITIVE_INFINITY;
      return da - db;
    });
    return withScores.slice(0, n).map((s) => s.t);
  }

  // =============================================
  // Fetch Purchase Orders
  // =============================================
  useEffect(() => {
    let mounted = true;

    async function fetchPOs() {
      if (!userId || !projId) return;
      setLoadingPOs(true);

      try {
        const res = await fetch(
          `${apiBase}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(
            projId
          )}`,
          { cache: 'no-store' }
        );

        if (!res.ok) throw new Error(`Server returned ${res.status}`);

        const json = await res.json();
        let arr = Array.isArray(json) ? json : json.items || json.Items || [];
        if (!Array.isArray(arr)) arr = [json];

        const normalized = arr.map((po) => ({
          id: po.id || po.poId || po.orderNumber,
          total: Number(po.total || 0),
          payments: Number(po.payments || 0),
          status: po.status || '',
        }));

        if (mounted) setPurchaseOrders(normalized);
      } catch (err) {
        console.warn('Failed to fetch POs', err);
        if (mounted) setPurchaseOrders([]);
      } finally {
        if (mounted) setLoadingPOs(false);
      }
    }

    fetchPOs();
    return () => (mounted = false);
  }, [apiBase, projId, userId]);

  // =============================================
  // Fetch Invoices
  // =============================================
  useEffect(() => {
    let mounted = true;

    async function fetchInvoices() {
      if (!userId || !projId) return;
      setLoadingInvoices(true);

      try {
        const res = await fetch(
          `${apiBase}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(projId)}`,
          { cache: 'no-store' }
        );

        if (!res.ok) throw new Error(`Server returned ${res.status}`);

        const json = await res.json();
        const arr = Array.isArray(json) ? json : json.items || json.Items || [];

        if (mounted) setInvoices(arr);
      } catch (err) {
        console.warn('Failed to fetch invoices', err);
        if (mounted) setInvoices([]);
      } finally {
        if (mounted) setLoadingInvoices(false);
      }
    }

    fetchInvoices();
    return () => (mounted = false);
  }, [apiBase, projId, userId]);

  // =============================================
  // Fetch Time Billing Invoices
  // =============================================
  useEffect(() => {
    let mounted = true;

    async function fetchTBInvoices() {
      if (!userId || !projId) return;
      setLoadingTB(true);

      try {
        const res = await fetch(
          `${apiBase}/api/timebilling-invoices/project/${userId}/${projId}`,
          { cache: 'no-store' }
        );

        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const json = await res.json();

        if (mounted) setTbInvoices(Array.isArray(json) ? json : []);
      } catch (err) {
        console.warn('Failed to fetch TB invoices:', err);
        if (mounted) setTbInvoices([]);
      } finally {
        if (mounted) setLoadingTB(false);
      }
    }

    fetchTBInvoices();
    return () => (mounted = false);
  }, [apiBase, projId, userId]);

  // ---- Toggle To-Do Completed ----
  async function toggleComplete(todo) {
    const updated = { ...todo, completed: !todo.completed };

    // update UI instantly
    setMiniTodos((prev) => prev.map((p) => (p.todoId === todo.todoId ? updated : p)));

    // update local cache
    try {
      const cached = loadTodosFromStorage(userId) || [];
      const merged = cached.map((c) =>
        c.todoId === todo.todoId ? { ...c, completed: updated.completed } : c
      );
      saveTodosToStorage(userId, merged);
    } catch {}

    // update server
    if (!userId) return;
    try {
      await fetch(
        `${apiBase}/api/todos/${encodeURIComponent(userId)}/${encodeURIComponent(todo.todoId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ completed: updated.completed }),
        }
      );
    } catch (err) {
      console.warn('Failed to persist todo completion change', err);
    }
  }

  async function handleCoverUpload(file) {
    const userId = localStorage.getItem('userId') || '';
    if (!file || !userId || !projId) return;

    const validation = validateWebImageFile(file);
    if (!validation.ok) {
      setCoverUploadError(validation.error);
      return;
    }

    setUploadingCover(true);
    setCoverUploadError('');

    try {
      const token = localStorage.getItem('idToken');
      const signRes = await fetch(
        `${apiBase}/api/upload/presigned-url?filename=${encodeURIComponent(file.name)}&type=${encodeURIComponent(validation.mime)}&size=${encodeURIComponent(file.size)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (!signRes.ok) throw new Error(`Sign request failed (${signRes.status})`);

      const sign = await signRes.json();
      const putUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
      const fileUrl = sign.fileUrl || sign.publicUrl;
      if (!putUrl || !fileUrl) throw new Error('Invalid signing response');

      const uploadRes = await fetch(putUrl, {
        method: 'PUT',
        headers: { 'Content-Type': validation.mime },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Upload failed (${uploadRes.status})`);

      const saveRes = await fetch(
        `${apiBase}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coverImageUrl: fileUrl }),
        }
      );
      if (!saveRes.ok) throw new Error(`Project save failed (${saveRes.status})`);

      if (typeof setProject === 'function') {
        setProject((prev) => ({ ...(prev || {}), coverImageUrl: fileUrl }));
      }
    } catch (err) {
      console.error('Cover upload failed', err);
      setCoverUploadError(err.message || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  }

  const onCoverFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleCoverUpload(file);
    e.target.value = '';
  };

  const onCoverDrop = async (e) => {
    e.preventDefault();
    setCoverDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await handleCoverUpload(file);
  };

  // ---- Pretty Date Format ----
  function prettyDate(d) {
    if (!d) return '';
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return d;
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  // =============================================
  // Totals for POs
  // =============================================
  const totals = useMemo(() => {
    const totalPO = purchaseOrders.reduce((s, p) => s + (Number(p.total) || 0), 0);
    const paidPO = purchaseOrders.reduce((s, p) => s + (Number(p.payments) || 0), 0);
    const openBalance = Math.max(0, totalPO - paidPO);

    return { totalPO, paidPO, openBalance };
  }, [purchaseOrders]);

  function formatStatusLabel(status) {
    if (!status) return 'Draft';
    const lower = status.toLowerCase();
    if (lower === 'changes_requested') return 'Changes Requested';
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  const invoiceTotalsByStatus = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      const status = formatStatusLabel(inv.status);
      const amt = getInvoiceRemaining(inv);
      map[status] = (map[status] || 0) + amt;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [invoices]);

  const invoiceTotalAmount = useMemo(
    () => invoices.reduce((s, inv) => s + getInvoiceRemaining(inv), 0),
    [invoices]
  );

  const INVOICE_COLORS = {
    approved: '#D9E0C7',
    sent: '#cbd5d8',
    draft: '#EBDDCC',
    'Changes Requested': '#f3e3d0',
    paid: '#D9E0C7',
  };

  const PO_COLORS = ['#D9E0C7', '#CAD4D8'];

  const TB_COLOR_MAP = {
    Paid: '#D9E0C7',
    Unpaid: '#CAD4D8',
  };

  const tbChartData = useMemo(() => {
    if (!tbInvoices || tbInvoices.length === 0) return [];

    let paid = 0;
    let unpaid = 0;

    tbInvoices.forEach((inv) => {
      const invoiceTotal = Number(inv?.totals?.total || 0);
      const remaining = getTimeBillingRemaining(inv);
      paid += Math.max(invoiceTotal - remaining, 0);
      unpaid += remaining;
    });

    return [
      { name: 'Paid', value: paid },
      { name: 'Unpaid', value: unpaid },
    ].filter((d) => d.value > 0);
  }, [tbInvoices]);

  const tbGrandTotal = useMemo(
    () => tbInvoices.reduce((s, inv) => s + getTimeBillingRemaining(inv), 0),
    [tbInvoices]
  );

  const invoiceChartData =
    invoiceTotalsByStatus.filter((d) => d.value > 0).length > 0
      ? invoiceTotalsByStatus.filter((d) => d.value > 0)
      : [{ name: 'No invoices', value: 1 }];

  const chartData = [
    { name: 'Paid', value: totals.paidPO },
    { name: 'Open Balance', value: totals.openBalance },
  ].filter((d) => d.value > 0);

  return (
    <div className="project-details-default content-wrap">
      <div className="project-hero">
        <div className="project-cover-wrap">
          <label
            htmlFor={coverInputId}
            style={{
              display: 'block',
              cursor: uploadingCover ? 'default' : 'pointer',
              outline: coverDragActive ? '2px dashed #7ca65c' : 'none',
              outlineOffset: 4,
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setCoverDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setCoverDragActive(false);
            }}
            onDrop={onCoverDrop}
          >
            <div
              className={`project-cover-img ${coverImageUrl ? 'has-image' : 'no-image'}`}
              style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})` } : undefined}
              role="img"
              aria-label="Project cover"
            />
          </label>
          <input
            id={coverInputId}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
            style={VISUALLY_HIDDEN_FILE_INPUT_STYLE}
            onChange={onCoverFileSelected}
          />
          {uploadingCover && <div className="project-cover-status">Uploading cover...</div>}
          {!uploadingCover && coverUploadError && (
            <div className="project-cover-status project-cover-status-error">{coverUploadError}</div>
          )}
        </div>
      </div>

      <div className="info-row">
        <div className="info-card">
          <div className="info-label">Project Site</div>
          <div className="info-value">
            {(projectSite || '—').split('\n').map((s, i) => (
              <div key={i}>{s || '—'}</div>
            ))}
          </div>
        </div>

        <div className="info-card">
          <div className="info-label">Billing Address</div>
          <div className="info-value">
            {(billingAddress || '—').split('\n').map((s, i) => (
              <div key={i}>{s || '—'}</div>
            ))}
          </div>
        </div>

        <div className="info-card">
          <div className="info-label">Customer Name</div>
          <div className="info-value">{customerName || '—'}</div>
        </div>

        <div className="info-card">
          <div className="info-label">Customer Contact</div>
          <div className="info-value">
            <div>
              {customerContact ? <a href={`mailto:${customerContact}`}>{customerContact}</a> : '—'}
            </div>
            <div>{customerPhone ? <a href={`tel:${customerPhone}`}>{customerPhone}</a> : '—'}</div>
          </div>
        </div>
      </div>

      {project?.pinterestBoardUrl && (
        <div className="info-row" style={{ marginTop: 0 }}>
          <a
            href={project.pinterestBoardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="info-card"
            style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: 'inherit' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#E60023" aria-hidden>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
            </svg>
            <div>
              <div className="info-label" style={{ marginBottom: 2 }}>Mood Board</div>
              <div className="info-value" style={{ fontSize: '0.8rem', color: '#E60023' }}>View on Pinterest ↗</div>
            </div>
          </a>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Invoices */}
        <div className="card">
          <h4>
            Invoices <span className="muted">Total Due: {formatCurrency(invoiceTotalAmount)}</span>
          </h4>
          <div className="card-body">
            {loadingInvoices ? (
              <div className="muted">Loading invoices…</div>
            ) : invoiceTotalsByStatus.length === 0 ? (
              <div className="muted">No invoices yet.</div>
            ) : (
              <div className="chart-responsive">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={invoiceChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                    >
                      {invoiceChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            INVOICE_COLORS[entry.name] || Object.values(INVOICE_COLORS)[index % 5]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Legend formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Todos */}
        <div className="card">
          <h4>To Do List</h4>
          <div className="card-body todo-list">
            {loadingTodos ? (
              <div className="muted">Loading tasks…</div>
            ) : miniTodos.length === 0 ? (
              <div className="muted">No tasks for this project.</div>
            ) : (
              <ul>
                {miniTodos.map((t) => (
                  <li key={t.todoId}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!t.completed}
                        onChange={() => toggleComplete(t)}
                      />
                      {t.title}
                      {t.assignees?.length > 0 && (
                        <span className="muted" style={{ marginLeft: 8 }}>
                          • {t.assignees.map((id) => assigneeName(id)).join(', ')}
                        </span>
                      )}
                      {t.dueDate ? (
                        <span className="muted" style={{ marginLeft: 8 }}>
                          • due {prettyDate(t.dueDate)}
                        </span>
                      ) : null}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Purchase Orders */}
        <div className="card">
          <h4>Purchase Orders</h4>
          <div className="card-body">
            {loadingPOs ? (
              <div className="muted">Loading purchase orders…</div>
            ) : totals.totalPO === 0 ? (
              <div className="muted">No purchase orders yet.</div>
            ) : (
              <div className="chart-responsive">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PO_COLORS[index % PO_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Legend formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Time Billing */}
        <div className="card">
          <h4>
            Time Billing <span className="muted">Total Due: {formatCurrency(tbGrandTotal)}</span>
          </h4>

          <div className="card-body">
            {loadingTB ? (
              <div className="muted">Loading time billing…</div>
            ) : tbChartData.length === 0 ? (
              <div className="muted">No time billing invoices yet.</div>
            ) : (
              <div className="chart-responsive">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={tbChartData}
                      dataKey="value"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                    >
                      {tbChartData.map((entry) => (
                        <Cell key={entry.name} fill={TB_COLOR_MAP[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
